import { z } from 'zod';

import { decimalSchema } from './common';

export const warehouseTypes = [
  'regular',
  'central',
  'transit',
  'virtual',
  'repair',
  'quarantine',
  'write_off',
] as const;
export const itemKinds = [
  'equipment',
  'tool',
  'consumable',
  'material',
  'spare_part',
  'product',
  'container',
  'other',
] as const;
export const trackingModes = ['quantity', 'lot', 'serial'] as const;
export const operationTypes = ['receipt', 'issue', 'transfer', 'write_off', 'adjustment'] as const;
export type OperationType = (typeof operationTypes)[number];

export const warehouseSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string(),
  warehouseType: z.string(),
  status: z.string(),
  isDefault: z.boolean(),
  address: z.string(),
});
export type Warehouse = z.infer<typeof warehouseSchema>;

export const createWarehouseSchema = z.object({
  code: z.string().trim().min(1, 'Введите код').max(32),
  name: z.string().trim().min(1, 'Введите название').max(200),
  warehouseType: z.enum(warehouseTypes),
  address: z.string().trim().max(500).default(''),
});
export type CreateWarehouse = z.infer<typeof createWarehouseSchema>;

/** The warehouse service keeps the code and the type once the warehouse exists. */
export const updateWarehouseSchema = createWarehouseSchema.pick({ name: true, address: true });
export type UpdateWarehouse = z.infer<typeof updateWarehouseSchema>;

export const unitSchema = z.object({ id: z.string(), name: z.string(), symbol: z.string() });
export type Unit = z.infer<typeof unitSchema>;

export const catalogItemSchema = z.object({
  id: z.string(),
  sku: z.string(),
  name: z.string(),
  description: z.string(),
  itemKind: z.string(),
  trackingMode: z.string(),
  baseUnitId: z.string(),
  /** Decimal string; empty when no minimum is set. */
  minimumStock: z.string(),
  status: z.string(),
});
export type CatalogItem = z.infer<typeof catalogItemSchema>;

export const createCatalogItemSchema = z.object({
  sku: z.string().trim().min(1, 'Введите артикул').max(64),
  name: z.string().trim().min(1, 'Введите название').max(200),
  itemKind: z.enum(itemKinds),
  trackingMode: z.enum(trackingModes),
  baseUnitId: z.string().min(1, 'Выберите единицу измерения'),
});
export type CreateCatalogItem = z.infer<typeof createCatalogItemSchema>;

export const catalogItemStatuses = ['active', 'inactive'] as const;

/** SKU, kind, unit and tracking are fixed once the item exists (warehouse service rule). */
export const updateCatalogItemSchema = z.object({
  name: z.string().trim().min(1, 'Введите название').max(200),
  description: z.string().trim().max(1000, 'Не длиннее 1000 символов').default(''),
  minimumStock: z.union([z.literal(''), decimalSchema]).default(''),
  status: z.enum(catalogItemStatuses),
});
export type UpdateCatalogItem = z.infer<typeof updateCatalogItemSchema>;

export const resourceParamsSchema = z.object({
  organizationId: z.coerce.number().int().positive(),
  id: z.string().min(1).max(64),
});

export const stockBalanceSchema = z.object({
  id: z.string(),
  warehouseId: z.string(),
  catalogItemId: z.string(),
  quantity: z.string(),
  reservedQuantity: z.string(),
  availableQuantity: z.string(),
});
export type StockBalance = z.infer<typeof stockBalanceSchema>;

export const stockOperationSchema = z.object({
  id: z.string(),
  operationNumber: z.string(),
  operationType: z.string(),
  status: z.string(),
  sourceWarehouseId: z.string().nullable(),
  destinationWarehouseId: z.string().nullable(),
  lineCount: z.number().int(),
  documentDate: z.string().nullable(),
});
export type StockOperation = z.infer<typeof stockOperationSchema>;

export const createStockOperationSchema = z.object({
  operationNumber: z.string().trim().min(1, 'Введите номер документа').max(64),
  operationType: z.enum(operationTypes),
  sourceWarehouseId: z.string().optional(),
  destinationWarehouseId: z.string().optional(),
  lines: z
    .array(
      z.object({
        catalogItemId: z.string().min(1, 'Выберите позицию'),
        unitId: z.string().min(1),
        quantity: decimalSchema,
      }),
    )
    .min(1, 'Добавьте хотя бы одну строку'),
});
export type CreateStockOperation = z.infer<typeof createStockOperationSchema>;

export const warehouseFilterSchema = z.object({ warehouseId: z.string().optional() });
export const catalogFilterSchema = z.object({ search: z.string().trim().max(100).optional() });
